/**
 * morenos-intro.js — Awwwards-Level Interactions
 * Entrance Animation × Magnetic Cursor × Card 3D Tilt × Scroll Reveal × Marquee
 * Inspiration: igloo.inc × landonorris.com × abeto.co
 */

(function () {
  'use strict';

  // ── Utils ──────────────────────────────────────────────────────
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isHomepage = () => window.location.pathname === '/' || window.location.pathname === '';

  // ── 1. ENTRANCE OVERLAY — igloo.inc door effect ──────────────
  // ONLY on homepage — direct URL visitors to /collections, /cart etc. must see content immediately
  function initEntrance() {
    if (!isHomepage()) return;
    if (prefersReducedMotion()) return;
    if (sessionStorage.getItem('morenos_entered')) return;

    const overlay = document.createElement('div');
    overlay.id = 'morenos-entrance';
    overlay.innerHTML = `
      <canvas class="entrance-particles" id="entrance-canvas"></canvas>
      <div class="entrance-logo">Moreno's</div>
      <div class="entrance-tagline">Personalisiert. Fair. Für dich.</div>
      <div class="entrance-cta"><span>— Tippen zum Betreten —</span></div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // Particle canvas
    initParticles('entrance-canvas');

    // Enter on click or after 4 seconds
    let exiting = false;
    function enter() {
      if (exiting) return;
      exiting = true;
      overlay.classList.add('is-exiting');
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
        sessionStorage.setItem('morenos_entered', '1');
      }, 1200);
    }

    overlay.addEventListener('click', enter);
    setTimeout(enter, 4800);
  }

  // ── 2. PARTICLE CANVAS — subtle floating dots ────────────────
  function initParticles(canvasId) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      o: Math.random() * 0.3 + 0.05,
    }));

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(196,53,94,${p.o})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      requestAnimationFrame(draw);
    }
    draw();
  }

  // ── 3. MAGNETIC CURSOR ────────────────────────────────────────
  function initCursor() {
    if (isMobile() || prefersReducedMotion()) return;

    const dot = document.createElement('div');
    dot.id = 'morenos-cursor';
    const ring = document.createElement('div');
    ring.id = 'morenos-cursor-follower';
    document.body.append(dot, ring);

    let mx = -100, my = -100;
    let rx = -100, ry = -100;

    document.addEventListener('mousemove', e => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.left = mx + 'px';
      dot.style.top  = my + 'px';
    });

    // Smooth follower with lerp
    function animateRing() {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      ring.style.left = rx + 'px';
      ring.style.top  = ry + 'px';
      requestAnimationFrame(animateRing);
    }
    animateRing();

    // Hover state
    const hoverEls = 'a, button, .card-wrapper, label, [role="button"], .link';
    document.addEventListener('mouseover', e => {
      if (e.target.closest(hoverEls)) document.body.classList.add('cursor-hover');
    });
    document.addEventListener('mouseout', e => {
      if (e.target.closest(hoverEls)) document.body.classList.remove('cursor-hover');
    });

    // Magnetic pull on buttons
    document.querySelectorAll('.button--primary, .newsletter-form__button').forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) * 0.25;
        const dy = (e.clientY - cy) * 0.25;
        btn.style.transform = `translate(${dx}px, ${dy}px) scale(1.02)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  // ── 4. CARD 3D TILT ──────────────────────────────────────────
  function initCardTilt() {
    if (isMobile() || prefersReducedMotion()) return;

    document.querySelectorAll('.card-wrapper').forEach((card, i) => {
      card.style.setProperty('--card-index', i % 4);

      card.addEventListener('mousemove', e => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width  - 0.5;
        const y = (e.clientY - rect.top)  / rect.height - 0.5;
        const tiltX = y * -10;
        const tiltY = x * 10;
        card.style.transform = `perspective(800px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-6px) scale(1.01)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
      });
    });
  }

  // ── 5. SCROLL REVEAL — Intersection Observer ─────────────────
  function initScrollReveal() {
    if (prefersReducedMotion()) return;

    // Add reveal classes to key elements
    const targets = [
      '.card-wrapper',
      '.featured-collection .collection__title',
      '.multicolumn-card',
      '.newsletter .section-header__title',
      '.collection-card',
      '.footer-block',
    ];

    targets.forEach(selector => {
      document.querySelectorAll(selector).forEach((el, i) => {
        el.classList.add('reveal-up');
        el.style.transitionDelay = `${i * 60}ms`;
      });
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal-up, .reveal-left').forEach(el => observer.observe(el));
  }

  // ── 6. MARQUEE STRIP — inject after hero (homepage only) ─────
  function injectMarquee() {
    if (!isHomepage()) return;

    const hero = document.querySelector('.shopify-section:has(.banner), .shopify-section .banner');
    const section = hero ? (hero.closest('.shopify-section') || hero) : null;

    if (!section) return;

    const words = [
      'Personalisiert', '✦', 'Fair Produziert', '✦',
      'Hunde-Designs', '✦', 'Dein Name', '✦',
      'Community Marktplatz', '✦', 'Schneller Versand', '✦',
      'Personalisiert', '✦', 'Fair Produziert', '✦',
      'Hunde-Designs', '✦', 'Dein Name', '✦',
      'Community Marktplatz', '✦', 'Schneller Versand', '✦',
    ];

    const marquee = document.createElement('div');
    marquee.className = 'morenos-marquee';
    marquee.innerHTML = `
      <div class="morenos-marquee-inner">
        ${words.map(w => w === '✦' ? '<span class="dot">✦</span>' : `<span>${w}</span>`).join('')}
      </div>
    `;

    section.insertAdjacentElement('afterend', marquee);
  }

  // ── 7. SMOOTH HEADER on scroll ───────────────────────────────
  function initStickyHeader() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    let lastY = 0;

    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (y > 80) {
        header.style.boxShadow = '0 2px 32px rgba(46,42,57,0.08)';
      } else {
        header.style.boxShadow = 'none';
      }
      lastY = y;
    }, { passive: true });
  }

  // ── 8. TEXT SPLIT REVEAL on hero heading (homepage only) ─────
  function initHeroReveal() {
    if (!isHomepage()) return;
    if (prefersReducedMotion()) return;
    const heading = document.querySelector('.banner__heading h1, .banner__heading');
    if (!heading) return;

    heading.style.opacity = '0';
    heading.style.transform = 'translateY(24px)';
    heading.style.transition = 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)';

    setTimeout(() => {
      heading.style.opacity = '1';
      heading.style.transform = 'translateY(0)';
    }, sessionStorage.getItem('morenos_entered') ? 100 : 5200);
  }

  // ── INIT ──────────────────────────────────────────────────────
  function init() {
    initEntrance();
    initCursor();
    initMarqueeOnLoad();
    initStickyHeader();
    initHeroReveal();

    // Defer heavier effects
    requestIdleCallback
      ? requestIdleCallback(() => { initCardTilt(); initScrollReveal(); injectMarquee(); })
      : setTimeout(() => { initCardTilt(); initScrollReveal(); injectMarquee(); }, 500);
  }

  function initMarqueeOnLoad() {
    // Try immediately, then retry if DOM not ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectMarquee);
    } else {
      injectMarquee();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
